import { Card, CardContent } from "@/components/ui/card";
import { useCurrency } from "@/hooks/use-currency";
import { useLanguage } from "@/contexts/language-context";
import { useCountUp } from "@/hooks/use-count-up";
import { AlertCircle, CheckCircle2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type FinancialHealth = "excellent" | "good" | "fair" | "poor";

interface WealthMeterProps {
  netWorth: number;
  totalAssets: number;
  totalDebts: number;
  savingsRate: number;
  financialHealth: FinancialHealth;
}

/**
 * Dashboard hero — "Wealth Meter".
 * Reads the financial position at a glance with gradient accent,
 * color-coded health badge, and a stacked asset/debt meter.
 */
export function WealthMeter({
  netWorth,
  totalAssets,
  totalDebts,
  savingsRate,
  financialHealth,
}: WealthMeterProps) {
  const { formatCurrency } = useCurrency();
  const { t } = useLanguage();
  const animatedNetWorth = useCountUp(Math.abs(netWorth), 1400);

  const total = totalAssets + totalDebts;
  const assetShare = total > 0 ? (totalAssets / total) * 100 : 0;
  const debtShare = total > 0 ? (totalDebts / total) * 100 : 0;
  const debtToAssetRatio = totalAssets > 0 ? (totalDebts / totalAssets) * 100 : 0;

  const healthConfig = {
    excellent: {
      classes: "bg-primary/15 text-primary",
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
      glow: "bg-primary/10",
    },
    good: {
      classes: "bg-[#89D7B7]/15 text-[#428475] dark:text-[#89D7B7]",
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
      glow: "bg-[#89D7B7]/10",
    },
    fair: {
      classes: "bg-amber-500/15 text-amber-500",
      icon: <AlertCircle className="h-3.5 w-3.5" />,
      glow: "bg-amber-500/10",
    },
    poor: {
      classes: "bg-destructive/15 text-destructive",
      icon: <AlertCircle className="h-3.5 w-3.5" />,
      glow: "bg-destructive/10",
    },
  }[financialHealth];

  return (
    <Card className="relative overflow-hidden border shadow-lg shadow-black/10 dark:shadow-black/40 animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both">
      {/* Gradient left accent bar — emerald → purple → teal */}
      <div
        className="absolute left-0 top-0 h-full w-1 rounded-l-xl"
        style={{
          background: "linear-gradient(to bottom, #34D399, #792CA2, #428475)",
        }}
      />

      {/* Ambient decorative glows */}
      <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-chart-1/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-chart-2/8 blur-3xl" />

      <CardContent className="p-6 sm:p-8 pl-7 sm:pl-9">
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          {/* Net worth figure */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium text-muted-foreground">
                {t("dashboard.netWorth")}
              </p>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize",
                  healthConfig.classes
                )}
              >
                {healthConfig.icon}
                {financialHealth}
              </span>
            </div>

            <p className="mt-2 text-4xl font-bold font-display tracking-tight sm:text-5xl">
              {netWorth < 0 ? "-" : ""}{formatCurrency(animatedNetWorth)}
            </p>

            {/* Mini chips: savings rate + DTI */}
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-muted-foreground">
                {savingsRate >= 0 ? (
                  <ArrowUpRight className="h-3 w-3 text-primary" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-destructive" />
                )}
                <span className="font-semibold text-foreground">{savingsRate.toFixed(1)}%</span>
                {t("dashboard.savingsRate").toLowerCase()}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-[#792CA2]" />
                <span className="font-semibold text-foreground">{debtToAssetRatio.toFixed(1)}%</span>
                {t("dashboard.debtToAssetRatio").toLowerCase()}
              </span>
            </div>
          </div>

          {/* Asset vs debt meter */}
          <div className="w-full lg:max-w-sm space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium">
                <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                {t("dashboard.assets")}
              </span>
              <span className="font-semibold">{formatCurrency(totalAssets)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium">
                <span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
                {t("dashboard.debts")}
              </span>
              <span className="font-semibold">{formatCurrency(totalDebts)}</span>
            </div>
            {/* Gradient stacked bar */}
            <div
              className="h-3.5 w-full overflow-hidden rounded-full bg-muted/80"
              role="progressbar"
              aria-label={t("dashboard.assetsVsDebts")}
              aria-valuenow={Math.round(assetShare)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="flex h-full w-full">
                <div
                  className="h-full"
                  style={{
                    width: `${assetShare}%`,
                    background: "linear-gradient(90deg, #34D399, #89D7B7)",
                  }}
                />
                <div
                  className="h-full"
                  style={{
                    width: `${debtShare}%`,
                    background: "linear-gradient(90deg, #C13383, #e85da0)",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}